/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { format } from 'date-fns';
import {
    NoMessagesContainer,
    StyledChatBodyContentNew,
    StyledChatBodyNew,
} from './IndividualChatBodyNew.styled';
import MessageItem from './MessageItem/MessageItem';
import ChatFooterNew from './ChatFooterNew/ChatFooterNew';
import { ChatReference } from 'api/rest/chat_new';
import { StatusesEnum } from './MessageItem/MessageStatuses';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';

export enum Sender {
    ME = 'ME',
    OTHER = 'OTHER',
    ADMIN = 'ADMIN',
}
interface IndividualChatBodyNewProps {
    checked: boolean;
    chatGroupId: string;
    chatTitle: string;
    messages: any;
    setMessages: (arg: any[]) => void;
    chatUserIds: number[];
    hasAccess?: boolean;
    initialMessage?: string;
    references?: ChatReference[];
}

export default function IndividualChatBodyNew({
    checked,
    chatGroupId,
    chatTitle,
    messages,
    setMessages,
    chatUserIds,
    hasAccess,
    initialMessage,
    references,
}: IndividualChatBodyNewProps) {
    const { language_setting: language } = useSelector((state: RootState) => state.user.entity);
    const { t } = useTranslation();

    const chatBodyContentRef = useRef<HTMLDivElement>(null);
    const footerRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const [contentHeight, setContentHeight] = useState('100%');
    const room = chatGroupId;

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            if (!footerRef.current || !bodyRef.current) return;

            const chaBodyHeaderHeight = 52;
            const mobileMargin = isMobile ? chaBodyHeaderHeight : 0;

            const footerHeight = footerRef.current.clientHeight;
            const contentHeightCalculation =
                bodyRef.current.clientHeight - footerHeight - mobileMargin;

            setContentHeight(`${contentHeightCalculation}px`);
        });

        if (footerRef.current) {
            resizeObserver.observe(footerRef.current);
        }

        return () => resizeObserver.disconnect();
    }, []);

    useEffect(() => {
        const chatBodyContent = chatBodyContentRef.current;
        const body = bodyRef.current;

        if (!chatBodyContent || !contentHeight || !body || messages.length < 1) {
            return;
        }

        chatBodyContent.scrollTop = chatBodyContent.scrollHeight;
    }, [contentHeight, messages]);

    return (
        <>
            <StyledChatBodyNew isChecked={checked} ref={bodyRef}>
                <StyledChatBodyContentNew heightValue={contentHeight} ref={chatBodyContentRef}>
                    {messages.length > 1 &&
                        messages.map((message: any, index: number) => {
                            const currentItemDate = format(new Date(message.createdAt), 'MMM d y');
                            const previousItemDate =
                                index === 0
                                    ? format(new Date(message.createdAt), 'MMM d y')
                                    : format(new Date(messages[index - 1]?.createdAt), 'MMM d y');

                            const showItemDate =
                                messages.length < 2
                                    ? false
                                    : index === 0
                                      ? true
                                      : currentItemDate !== previousItemDate;

                            return (
                                <MessageItem
                                    showItemDate={showItemDate}
                                    message={message.message}
                                    id={message.id}
                                    time={message.createdAt}
                                    sender={message.sender}
                                    key={index}
                                    author={
                                        message.sender === Sender.ME
                                            ? t('you', { lng: language })
                                            : message.user
                                    }
                                    status={
                                        message.read ? StatusesEnum.READ : StatusesEnum.DELIVERED
                                    }
                                    references={message.references}
                                />
                            );
                        })}
                    {messages.length <= 1 && (
                        <NoMessagesContainer>
                            <div>{t('noMessagesYet', { lng: language })}</div>
                        </NoMessagesContainer>
                    )}
                </StyledChatBodyContentNew>
                <ChatFooterNew
                    ref={footerRef}
                    setMessages={setMessages}
                    room={room}
                    chatUserIds={chatUserIds}
                    hasAccess={hasAccess}
                    initialMessage={initialMessage}
                    references={references}
                />
            </StyledChatBodyNew>
        </>
    );
}
